﻿import { CitationInfo, MessageContent } from './types';
import escapeRegExp from '../../lib/escapeRegExp.ts';

const replaceCitationPlaceholdersFallback = (
    text: string,
    citations: CitationInfo[]
): string => {
    // Capture all content after first †
    // copes with malformed citations e.g. 【4†QA_35742877-c9a6-48e5-ad92-3afd1d0d4bae†QA_35742877】【0†QA_e717e7c4-fbb7-4aa5-9a8e-00d4753985f9.txt†source】
    const placeholderRegex = /【[^】]*?†([^】]*)】/g;

    const replacedText = text.replace(
        placeholderRegex,
        (match, capturedContent) => {
            const potentialIdentifiers = capturedContent.split('†');

            const citation = citations.find((c) => {
                return potentialIdentifiers.some((identifier: string) => {
                    const cleanIdentifier = identifier.trim();
                    return (
                        c.fileId === cleanIdentifier ||
                        c.fileName === cleanIdentifier ||
                        c.fileName === cleanIdentifier + '.txt'
                    );
                });
            });

            if (citation) {
                return `[${citation.title}](#citation-${citation.fileId})`;
            }

            return '';
        }
    );

    return replacedText;
};

const replaceCitationPlaceholders = (
    content: MessageContent,
    citations: CitationInfo[]
): string => {
    const { text, textAnnotations } = content;

    let replacedText = text ?? '';

    if (textAnnotations && textAnnotations.length > 0) {
        // Map to store placeholders and their corresponding replacements
        const placeholderMap = new Map<string, string>();
        let placeholderIndex = 0;

        textAnnotations.forEach((annotation) => {
            const { textToReplace, inputFileId } = annotation;
            if (!textToReplace || !inputFileId) {
                return;
            }

            const citation = citations.find((c) => c.fileId === inputFileId);

            // Generate a unique placeholder
            const placeholder = `__PLACEHOLDER_${placeholderIndex}__`;
            placeholderIndex += 1;

            // Store the replacement in the map
            const replacement = citation
                ? `[${citation.title}](#citation-${citation.fileId})`
                : '';
            placeholderMap.set(placeholder, replacement);

            // Replace textToReplace with the placeholder
            replacedText = replacedText.replace(
                new RegExp(escapeRegExp(textToReplace), 'g'),
                placeholder
            );
        });

        // Replace all placeholders with their final replacements
        placeholderMap.forEach((replacement, placeholder) => {
            replacedText = replacedText.replace(
                new RegExp(escapeRegExp(placeholder), 'g'),
                replacement
            );
        });
    } else {
        replacedText = replaceCitationPlaceholdersFallback(
            replacedText,
            citations
        );
    }

    return replacedText;
};

export default replaceCitationPlaceholders;
